import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Shield, Users, Coins, Wand2, Ticket, Settings2, TrendingUp, Mail,
  Save, Trash2, Plus, ExternalLink, Sparkles, Lock, Percent, RefreshCw, Activity,
} from "lucide-react";
import { motion } from "framer-motion";
import ReliabilityTab from "@/pages/AdminReliability";

export default function Admin() {
  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1600px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">
          <Shield className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={1.5} />
          OWNER · ADMIN · CONTROLS
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Command <span className="font-bold text-amber-400">centre.</span>
        </h1>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-[#121214] border border-white/5 p-1 h-auto flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Overview
          </TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Wand2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Providers
          </TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Coins className="w-3.5 h-3.5" strokeWidth={1.5} /> Packages
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Users
          </TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Ticket className="w-3.5 h-3.5" strokeWidth={1.5} /> Coupons
          </TabsTrigger>
          <TabsTrigger value="margin" data-testid="tab-margin" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Percent className="w-3.5 h-3.5" strokeWidth={1.5} /> Margin
          </TabsTrigger>
          <TabsTrigger value="affiliate" data-testid="tab-affiliate" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Affiliate
          </TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Emails
          </TabsTrigger>
          <TabsTrigger value="reliability" data-testid="tab-reliability" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Activity className="w-3.5 h-3.5" strokeWidth={1.5} /> Reliability
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
            <Settings2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="providers"><ProvidersTab /></TabsContent>
        <TabsContent value="packages"><PackagesTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="coupons"><CouponsTab /></TabsContent>
        <TabsContent value="margin"><MarginTab /></TabsContent>
        <TabsContent value="affiliate"><AffiliateTab /></TabsContent>
        <TabsContent value="emails"><EmailsTab /></TabsContent>
        <TabsContent value="reliability"><ReliabilityTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Overview ---------- */
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [mrr, setMrr] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setStats(r.data));
    api.get("/admin/mrr").then((r) => setMrr(r.data)).catch(() => {});
  }, []);
  if (!stats) return <div className="text-zinc-500 text-sm">Loading…</div>;

  const rev = (stats.revenue_cents / 100).toFixed(2);
  const cost = (stats.ai_cost_cents / 100).toFixed(2);
  const profit = (stats.profit_cents / 100).toFixed(2);

  return (
    <div className="space-y-6">
      {/* MRR row */}
      {mrr && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="mrr-cards">
          <StatCard label="MRR" value={`€${mrr.mrr_eur.toFixed(0)}`} color="emerald" accent testid="stat-mrr" />
          <StatCard label="ARR" value={`€${mrr.arr_eur.toFixed(0)}`} color="emerald" testid="stat-arr" />
          <StatCard label="ACTIVE · SUBS" value={mrr.active_subscribers} testid="stat-active-subs" />
          <StatCard label="NEW · 30D" value={`+${mrr.new_subs_30d}`} color="emerald" testid="stat-new-30d" />
          <StatCard
            label="CHURN · 30D"
            value={mrr.churned_30d ? `-${mrr.churned_30d}` : "0"}
            color={mrr.churned_30d ? "amber" : undefined}
            testid="stat-churn-30d"
          />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="TOTAL REVENUE" value={`€${rev}`} accent testid="stat-revenue" />
        <StatCard label="AI COST" value={`€${cost}`} testid="stat-cost" />
        <StatCard label="PROFIT" value={`€${profit}`} color="emerald" testid="stat-profit" />
        <StatCard label="MARGIN" value={`${stats.margin_percent}%`} color="amber" testid="stat-margin" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="USERS" value={stats.users_count} testid="stat-users" />
        <StatCard label="PROJECTS" value={stats.projects_count} testid="stat-projects" />
        <StatCard label="CREDITS SOLD" value={stats.credits_sold?.toLocaleString() ?? 0} testid="stat-sold" />
        <StatCard label="CREDITS USED" value={stats.credits_consumed?.toLocaleString() ?? 0} testid="stat-used" />
      </div>

      {/* MRR breakdown by tier */}
      {mrr && Object.keys(mrr.tiers || {}).length > 0 && (
        <div className="bg-[#121214] border border-white/5 rounded-lg p-6" data-testid="mrr-breakdown">
          <div className="font-mono-tech text-[10px] text-amber-400 mb-4">MRR · BY · TIER</div>
          <div className="space-y-3">
            {Object.entries(mrr.tiers).map(([tierId, t]) => {
              const totalSubs = t.monthly_subs + t.yearly_subs;
              const pct = mrr.mrr_cents ? (t.mrr_cents / mrr.mrr_cents * 100) : 0;
              return (
                <div key={tierId} className="flex items-center gap-3">
                  <div className="w-24 text-sm font-medium">{t.plan_name}</div>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="text-xs font-mono-tech text-zinc-400 w-16 text-right">€{t.mrr_eur.toFixed(0)}</div>
                  <div className="text-xs font-mono-tech text-zinc-500 w-24 text-right">
                    {totalSubs} sub{totalSubs !== 1 ? "s" : ""} ({t.monthly_subs}M / {t.yearly_subs}Y)
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
        <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">MOST · USED · PROVIDERS</div>
        {Object.keys(stats.provider_usage || {}).length === 0 ? (
          <div className="text-zinc-500 text-sm">No videos generated yet.</div>
        ) : (
          <div className="space-y-2">
            {Object.entries(stats.provider_usage).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
              <div key={name} className="flex items-center gap-3">
                <div className="w-40 text-sm font-medium truncate">{name}</div>
                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, count * 10)}%` }} />
                </div>
                <div className="text-xs font-mono-tech text-zinc-400">{count}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TierAnalyticsSection />
    </div>
  );
}

/* ---------- Phase 2 · Tier Analytics ---------- */
function TierAnalyticsSection() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/tiers").then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;

  const tierColors = {
    smart: "bg-amber-500",
    budget: "bg-emerald-500",
    premium: "bg-sky-500",
    ultra: "bg-fuchsia-500",
    preview: "bg-zinc-500",
    manual: "bg-white/60",
    unknown: "bg-white/20",
  };
  const tierOrder = ["smart", "budget", "premium", "ultra", "preview", "manual", "unknown"];
  const totalTier = Object.values(d.tier_popularity).reduce((a, b) => a + b, 0) || 1;
  const totalCost = (d.fal.cost_eur || 0) + (d.emergent.cost_eur || 0);

  return (
    <div className="space-y-6" data-testid="tier-analytics">
      <div className="flex items-baseline justify-between pt-4 border-t border-white/5">
        <div>
          <div className="font-mono-tech text-[10px] text-amber-400">PHASE · 2 · BUSINESS · DATA</div>
          <div className="font-heading text-2xl font-bold tracking-tight mt-1">Tier analytics · last 30 days</div>
          <div className="text-xs text-zinc-500 mt-1">
            Read-only signals to inform Phase 3 pricing decisions — no user-facing price changes yet.
          </div>
        </div>
        <Badge className="bg-white/5 text-zinc-300 border-white/10 font-mono-tech text-[9px]">
          {d.projects_completed} projects · {d.window_days}d
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="ACTIVE · CREATORS" value={d.credits.active_users} testid="stat-active-creators" />
        <StatCard label="AVG · CREDITS · / · USER" value={d.credits.avg_per_active_user} color="amber" testid="stat-avg-credits" />
        <StatCard label="FAL · SPEND" value={`€${d.fal.cost_eur.toFixed(2)}`} testid="stat-fal-spend" />
        <StatCard label="EMERGENT · SPEND" value={`€${d.emergent.cost_eur.toFixed(2)}`} testid="stat-emergent-spend" />
      </div>

      <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
        <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">TIER · POPULARITY</div>
        {totalTier <= 1 ? (
          <div className="text-zinc-500 text-sm">No completed generations in the last 30 days.</div>
        ) : (
          <div className="space-y-2">
            {tierOrder.filter((k) => (d.tier_popularity[k] || 0) > 0).map((k) => {
              const count = d.tier_popularity[k];
              const pct = (count / totalTier) * 100;
              return (
                <div key={k} className="flex items-center gap-3">
                  <div className="w-24 text-sm font-medium capitalize">{k}</div>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${tierColors[k] || "bg-white/40"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="text-xs font-mono-tech text-zinc-400 w-14 text-right">{pct.toFixed(0)}%</div>
                  <div className="text-xs font-mono-tech text-zinc-500 w-14 text-right">{count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
          <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">SMART · AI · PROVIDER · MIX</div>
          {d.smart_provider_mix.length === 0 ? (
            <div className="text-zinc-500 text-sm">Smart AI has not been used yet.</div>
          ) : (
            <div className="space-y-2">
              {d.smart_provider_mix.map(({ name, count }) => {
                const totalSmart = d.smart_provider_mix.reduce((a, b) => a + b.count, 0);
                const pct = totalSmart ? (count / totalSmart) * 100 : 0;
                return (
                  <div key={name} className="flex items-center gap-3">
                    <div className="w-32 text-sm font-medium truncate">{name}</div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="text-xs font-mono-tech text-zinc-400 w-10 text-right">{count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
          <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">FAL · vs · EMERGENT · SPEND</div>
          {totalCost === 0 ? (
            <div className="text-zinc-500 text-sm">No paid clips yet.</div>
          ) : (
            <>
              <div className="w-full h-3 rounded-full overflow-hidden flex bg-white/5">
                <div className="h-full bg-sky-500" style={{ width: `${(d.fal.cost_eur / totalCost) * 100}%` }} />
                <div className="h-full bg-emerald-500" style={{ width: `${(d.emergent.cost_eur / totalCost) * 100}%` }} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs font-mono-tech">
                <div><span className="inline-block w-2 h-2 rounded-full bg-sky-500 mr-1.5" />Fal · €{d.fal.cost_eur.toFixed(2)} · {d.fal.clips} clips</div>
                <div><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1.5" />Emergent · €{d.emergent.cost_eur.toFixed(2)} · {d.emergent.clips} clips</div>
                <div className="text-zinc-500">FFmpeg · {d.ffmpeg.clips} clips (free)</div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
        <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">CREDIT · SPEND · DISTRIBUTION (per user, 30d)</div>
        {Object.keys(d.credits.distribution).length === 0 ? (
          <div className="text-zinc-500 text-sm">No credit spend yet.</div>
        ) : (
          <div className="space-y-2">
            {["0-9", "10-29", "30-59", "60-119", "120-249", "250+"]
              .filter((b) => d.credits.distribution[b])
              .map((b) => {
                const count = d.credits.distribution[b];
                const pct = (count / d.credits.active_users) * 100;
                return (
                  <div key={b} className="flex items-center gap-3">
                    <div className="w-24 text-sm font-mono-tech text-zinc-400">{b}</div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="text-xs font-mono-tech text-zinc-500 w-14 text-right">{count} users</div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Providers ---------- */
function ProvidersTab() {
  const [providers, setProviders] = useState([]);
  const [edits, setEdits] = useState({});
  const load = async () => setProviders((await api.get("/providers")).data);
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    const patch = edits[id];
    if (!patch) return;
    try {
      await api.patch(`/providers/${id}`, patch);
      toast.success("Provider saved");
      setEdits((e) => ({ ...e, [id]: undefined }));
      load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-zinc-500 mb-3">
        Adjust the credit price your customers pay and the AI cost you incur. Margin is calculated live.
      </div>
      {providers.map((p) => {
        const e = edits[p.id] || {};
        const price = e.credits_per_clip ?? p.credits_per_clip;
        const cost = e.ai_cost_cents ?? p.ai_cost_cents;
        const enabled = e.enabled ?? p.enabled;
        // Selling price EUR per credit assumed at €0.05 (based on Starter €29/100)
        const pricePerCredit = 29 / 100;
        const sellPrice = price * pricePerCredit;
        const profit = sellPrice - cost / 100;
        const margin = sellPrice ? (profit / sellPrice * 100) : 0;
        return (
          <div key={p.id} className="bg-[#121214] border border-white/5 rounded-lg p-4"
            data-testid={`provider-row-${p.id}`}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-bold text-sm tracking-tight">{p.name}</div>
                  <Badge className="text-[9px] font-mono-tech uppercase bg-white/10 text-zinc-300 border-white/10">{p.tier.replace("_", " ")}</Badge>
                  {p.requires_key && !p.enabled_key && (
                    <Lock className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5} />
                  )}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1 font-mono-tech">{p.model_id}</div>
              </div>
              <div className="lg:col-span-2">
                <Label className="font-mono-tech text-[9px] text-zinc-500">CREDITS/CLIP</Label>
                <Input type="number" value={price}
                  onChange={(ev) => setEdits({ ...edits, [p.id]: { ...e, credits_per_clip: Number(ev.target.value) } })}
                  data-testid={`provider-price-${p.id}`}
                  className="mt-1 h-9 bg-[#0b0b0d] border-white/10" />
              </div>
              <div className="lg:col-span-2">
                <Label className="font-mono-tech text-[9px] text-zinc-500">AI COST (¢)</Label>
                <Input type="number" value={cost}
                  onChange={(ev) => setEdits({ ...edits, [p.id]: { ...e, ai_cost_cents: Number(ev.target.value) } })}
                  data-testid={`provider-cost-${p.id}`}
                  className="mt-1 h-9 bg-[#0b0b0d] border-white/10" />
              </div>
              <div className="lg:col-span-2 text-right">
                <div className="font-mono-tech text-[9px] text-zinc-500">SELL €{sellPrice.toFixed(2)}</div>
                <div className={`text-sm font-heading font-bold ${margin >= 50 ? "text-emerald-400" : margin >= 20 ? "text-amber-400" : "text-red-400"}`}>
                  {margin.toFixed(0)}% margin
                </div>
              </div>
              <div className="lg:col-span-1 flex items-center justify-center">
                <Switch checked={enabled}
                  onCheckedChange={(v) => setEdits({ ...edits, [p.id]: { ...e, enabled: v } })}
                  data-testid={`provider-enabled-${p.id}`} />
              </div>
              <div className="lg:col-span-1 flex justify-end">
                <Button size="sm" onClick={() => save(p.id)}
                  disabled={!edits[p.id]} data-testid={`save-provider-${p.id}`}
                  className="bg-amber-500 hover:bg-amber-600 text-black h-9">
                  <Save className="w-3.5 h-3.5" strokeWidth={2} />
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Packages ---------- */
function PackagesTab() {
  const [pkgs, setPkgs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", credits: 100, price_cents: 2900, bonus_credits: 0, discount_percent: 0, sort_order: 0, highlight: false, active: true });

  const load = async () => setPkgs((await api.get("/admin/packages")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/packages", form);
      toast.success("Package created");
      setCreating(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete package?")) return;
    await api.delete(`/admin/packages/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} data-testid="create-package"
          className="bg-amber-500 hover:bg-amber-600 text-black">
          <Plus className="w-4 h-4 mr-1" strokeWidth={2} /> New package
        </Button>
      </div>
      {creating && (
        <div className="bg-[#121214] border border-amber-500/40 rounded-lg p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0b0b0d] border-white/10" />
            <Input type="number" placeholder="Credits" value={form.credits} onChange={(e) => setForm({ ...form, credits: Number(e.target.value) })} className="bg-[#0b0b0d] border-white/10" />
            <Input type="number" placeholder="Price (cents)" value={form.price_cents} onChange={(e) => setForm({ ...form, price_cents: Number(e.target.value) })} className="bg-[#0b0b0d] border-white/10" />
            <Input type="number" placeholder="Bonus" value={form.bonus_credits} onChange={(e) => setForm({ ...form, bonus_credits: Number(e.target.value) })} className="bg-[#0b0b0d] border-white/10" />
          </div>
          <div className="flex gap-2 mt-3 justify-end">
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} className="bg-amber-500 hover:bg-amber-600 text-black">Create</Button>
          </div>
        </div>
      )}
      {pkgs.map((p) => (
        <div key={p.id} className="bg-[#121214] border border-white/5 rounded-lg p-4 flex items-center justify-between" data-testid={`admin-package-${p.id}`}>
          <div>
            <div className="font-heading font-bold tracking-tight">{p.name}</div>
            <div className="text-xs text-zinc-500 mt-1">
              {p.credits} credits {p.bonus_credits > 0 && `+ ${p.bonus_credits} bonus`} · €{(p.price_cents / 100).toFixed(2)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {p.highlight && <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[9px]">FEATURED</Badge>}
            {!p.active && <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[9px]">INACTIVE</Badge>}
            <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="text-red-400 hover:bg-red-500/10">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Users ---------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [grant, setGrant] = useState({});
  const load = async () => setUsers((await api.get("/admin/users")).data);
  useEffect(() => { load(); }, []);
  const send = async (uid) => {
    const g = grant[uid];
    if (!g?.credits) return;
    try {
      await api.post(`/admin/users/${uid}/grant`, { user_id: uid, credits: Number(g.credits), note: g.note || "" });
      toast.success("Credits granted");
      setGrant((s) => ({ ...s, [uid]: {} })); load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-2">
      {users.map((u) => (
        <div key={u.id} className="bg-[#121214] border border-white/5 rounded-lg p-4"
          data-testid={`user-row-${u.id}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
            <div className="lg:col-span-4">
              <div className="font-medium">{u.name}</div>
              <div className="text-xs text-zinc-500">{u.email}</div>
            </div>
            <div className="lg:col-span-2">
              <Badge className={`text-[9px] uppercase ${u.role === "owner" ? "bg-amber-500/20 text-amber-300" : "bg-white/10 text-zinc-300"}`}>{u.role}</Badge>
            </div>
            <div className="lg:col-span-2 text-sm">
              <div className="font-mono-tech text-[9px] text-zinc-500">CREDITS</div>
              <div className="font-heading font-bold">{u.role === "owner" ? "∞" : (u.credits_balance || 0)}</div>
            </div>
            <div className="lg:col-span-1 text-sm">
              <div className="font-mono-tech text-[9px] text-zinc-500">PROJECTS</div>
              <div className="font-heading font-bold">{u.projects_count || 0}</div>
            </div>
            <div className="lg:col-span-2">
              <Input type="number" placeholder="+credits"
                value={grant[u.id]?.credits || ""}
                onChange={(e) => setGrant({ ...grant, [u.id]: { ...(grant[u.id] || {}), credits: e.target.value } })}
                data-testid={`grant-input-${u.id}`}
                className="bg-[#0b0b0d] border-white/10 h-9" />
            </div>
            <div className="lg:col-span-1 flex justify-end">
              {u.role !== "owner" && (
                <Button size="sm" onClick={() => send(u.id)} data-testid={`grant-btn-${u.id}`}
                  className="bg-amber-500 hover:bg-amber-600 text-black h-9">
                  Grant
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Coupons ---------- */
function CouponsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", discount_percent: 10 });
  const load = async () => setItems((await api.get("/admin/coupons")).data);
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/admin/coupons", form);
      toast.success("Coupon created");
      setForm({ code: "", discount_percent: 10 }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="bg-[#121214] border border-white/5 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[140px]">
          <Label className="font-mono-tech text-[10px] text-zinc-500">CODE</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="SUMMER30" data-testid="new-coupon-code"
            className="mt-1 bg-[#0b0b0d] border-white/10 h-9 font-mono-tech" />
        </div>
        <div className="w-32">
          <Label className="font-mono-tech text-[10px] text-zinc-500">DISCOUNT %</Label>
          <Input type="number" value={form.discount_percent}
            onChange={(e) => setForm({ ...form, discount_percent: Number(e.target.value) })}
            data-testid="new-coupon-percent"
            className="mt-1 bg-[#0b0b0d] border-white/10 h-9" />
        </div>
        <Button onClick={create} data-testid="create-coupon" className="bg-amber-500 hover:bg-amber-600 text-black h-9">
          <Plus className="w-4 h-4 mr-1" strokeWidth={2} /> Create
        </Button>
      </div>
      {items.map((c) => (
        <div key={c.id} className="bg-[#121214] border border-white/5 rounded-lg p-4 flex items-center justify-between"
          data-testid={`coupon-${c.id}`}>
          <div>
            <div className="font-mono-tech font-bold text-amber-400">{c.code}</div>
            <div className="text-xs text-zinc-500 mt-1">
              {c.discount_percent}% off · used {c.uses}{c.max_uses ? `/${c.max_uses}` : ""} times
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      ))}
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsTab() {
  const [falKey, setFalKey] = useState("");
  const [hasKey, setHasKey] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [refCfg, setRefCfg] = useState({ signup_bonus: 25, referrer_bonus: 50, referee_bonus: 25 });

  useEffect(() => {
    api.get("/providers/_meta/has_fal_key").then((r) => setHasKey(r.data.has_fal_key));
    api.get("/admin/stripe/info").then((r) => setStripe(r.data));
    api.get("/admin/referral").then((r) => setRefCfg(r.data));
  }, []);

  const saveFal = async () => {
    try {
      const { data } = await api.post("/providers/_meta/fal_key", { fal_key: falKey });
      setHasKey(data.has_fal_key);
      toast.success(data.has_fal_key ? "fal.ai key saved" : "fal.ai key cleared");
      setFalKey("");
    } catch { toast.error("Save failed"); }
  };
  const saveRef = async () => {
    try { await api.put("/admin/referral", refCfg); toast.success("Referral saved"); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* fal.ai */}
      <div className="bg-[#121214] border border-white/5 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <Wand2 className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
          <div className="font-heading font-bold tracking-tight">fal.ai API Key</div>
          {hasKey && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[9px]">ACTIVE</Badge>}
        </div>
        <p className="text-xs text-zinc-400 mb-3">
          Unlocks Kling, Veo, Runway, Luma and MiniMax providers.{" "}
          <a href="https://fal.ai/dashboard/keys" target="_blank" rel="noreferrer" className="text-amber-400 hover:underline">Get your key</a>
        </p>
        <div className="flex gap-2">
          <Input type="password" value={falKey} onChange={(e) => setFalKey(e.target.value)}
            placeholder={hasKey ? "•••••••••••••••••" : "fal-… or key_id:key_secret"}
            data-testid="fal-key-input"
            className="bg-[#0b0b0d] border-white/10 h-10 font-mono" />
          <Button onClick={saveFal} data-testid="fal-key-save"
            className="bg-amber-500 hover:bg-amber-600 text-black h-10">Save</Button>
        </div>
        {hasKey && (
          <Button variant="ghost" size="sm" onClick={() => { setFalKey(""); saveFal(); }}
            className="mt-2 text-red-400 hover:bg-red-500/10 text-xs">Clear key</Button>
        )}
      </div>

      {/* Stripe */}
      <div className="bg-[#121214] border border-white/5 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <Coins className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
          <div className="font-heading font-bold tracking-tight">Stripe</div>
          {stripe?.configured && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[9px]">CONFIGURED · {stripe.mode?.toUpperCase()}</Badge>}
        </div>
        <p className="text-xs text-zinc-400 mb-3">
          Sandbox is active. When ready to accept real payments, claim your Stripe account below — Emergent will auto-switch to live keys after KYC.
        </p>
        {stripe?.onboarding_url && (
          <a href={stripe.onboarding_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm">
            Claim Stripe account <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
          </a>
        )}
      </div>

      {/* Referral */}
      <div className="bg-[#121214] border border-white/5 rounded-lg p-5 lg:col-span-2">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
          <div className="font-heading font-bold tracking-tight">Referral & signup bonuses</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">SIGNUP BONUS</Label>
            <Input type="number" value={refCfg.signup_bonus}
              onChange={(e) => setRefCfg({ ...refCfg, signup_bonus: Number(e.target.value) })}
              data-testid="ref-signup" className="mt-1 bg-[#0b0b0d] border-white/10" />
          </div>
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">REFERRER BONUS</Label>
            <Input type="number" value={refCfg.referrer_bonus}
              onChange={(e) => setRefCfg({ ...refCfg, referrer_bonus: Number(e.target.value) })}
              data-testid="ref-referrer" className="mt-1 bg-[#0b0b0d] border-white/10" />
          </div>
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">REFEREE BONUS</Label>
            <Input type="number" value={refCfg.referee_bonus}
              onChange={(e) => setRefCfg({ ...refCfg, referee_bonus: Number(e.target.value) })}
              data-testid="ref-referee" className="mt-1 bg-[#0b0b0d] border-white/10" />
          </div>
        </div>
        <Button onClick={saveRef} data-testid="save-referral"
          className="mt-4 bg-amber-500 hover:bg-amber-600 text-black h-9">
          <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save
        </Button>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = false, color, testid }) {
  const colorClass = color === "emerald" ? "text-emerald-400" : color === "amber" ? "text-amber-400" : accent ? "text-amber-400" : "text-white";
  return (
    <div data-testid={testid} className="bg-[#121214] border border-white/5 rounded-lg p-4">
      <div className="font-mono-tech text-[9px] text-zinc-500 mb-2">{label}</div>
      <div className={`font-heading text-2xl font-bold tracking-tighter ${colorClass}`}>{value}</div>
    </div>
  );
}


/* ---------- Emails · transactional log (Phase 16) ---------- */
function EmailsTab() {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = async (cursor) => {
    const params = { limit: 100 };
    if (cursor) params.before = cursor;
    const { data } = await api.get("/admin/emails", { params });
    return data;
  };

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchPage(null);
        setEvents(d.events || []);
        setCounts(d.counts_by_type || {});
        setNextCursor(d.next_cursor || null);
      } finally { setLoading(false); }
    })();
  }, []);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const d = await fetchPage(nextCursor);
      setEvents((prev) => [...prev, ...(d.events || [])]);
      setNextCursor(d.next_cursor || null);
    } finally { setLoadingMore(false); }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading email events…</div>;
  const eventColor = {
    sent: "bg-sky-500/20 text-sky-300 border-sky-500/30",
    delivered: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    opened: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    clicked: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30",
    bounced: "bg-red-500/20 text-red-300 border-red-500/30",
    complained: "bg-red-500/30 text-red-200 border-red-500/40",
    send_failed: "bg-red-500/20 text-red-300 border-red-500/30",
    skipped_not_configured: "bg-white/5 text-zinc-400 border-white/10",
    unknown: "bg-white/5 text-zinc-400 border-white/10",
  };

  return (
    <div className="space-y-8" data-testid="admin-emails">
      <div>
        <div className="font-mono-tech text-[10px] text-amber-400">TRANSACTIONAL · LOG</div>
        <div className="font-heading text-2xl font-bold tracking-tight mt-1">Email deliverability</div>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Every welcome, team invite and API-key notification we send. Configure
          Resend webhooks (Dashboard → Webhooks → POST to <code className="text-amber-400">/api/webhooks/resend</code>)
          to also capture delivered / opened / clicked / bounced events.
          The log auto-prunes after 90 days.
        </p>
      </div>

      {/* Counts (30-day window) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="admin-emails-counts">
        {["sent", "delivered", "opened", "clicked", "bounced", "send_failed"].map((et) => (
          <div key={et} className="p-3 rounded border border-white/5 bg-[#121214]">
            <div className="font-mono-tech text-[9px] text-zinc-500 mb-1 uppercase">{et.replace("_", " ")}</div>
            <div className="font-heading text-xl font-bold tracking-tight">{counts[et] || 0}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-white/5 bg-[#121214] overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
          <div className="font-mono-tech text-[10px] text-zinc-500">
            EVENTS · SHOWING · {events.length}
          </div>
        </div>
        {events.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            No email events yet. Emails will appear here after the first signup / invite / API-key mint.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {events.map((e) => (
              <div key={e.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`email-event-${e.id}`}>
                <span className={`font-mono-tech text-[9px] font-bold px-2 py-0.5 rounded border uppercase whitespace-nowrap ${eventColor[e.event_type] || eventColor.unknown}`}>
                  {e.event_type || "?"}
                </span>
                <span className="font-mono-tech text-[10px] text-zinc-500 uppercase min-w-[100px]">
                  {e.kind || "-"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{e.subject || "(no subject)"}</div>
                  <div className="text-[11px] text-zinc-500 truncate">to {e.to || "(unknown)"}</div>
                </div>
                <div className="text-[10px] font-mono text-zinc-500 whitespace-nowrap">
                  {e.created_at ? new Date(e.created_at).toLocaleString() : "-"}
                </div>
              </div>
            ))}
          </div>
        )}
        {nextCursor && (
          <div className="p-3 border-t border-white/5 flex justify-center">
            <Button
              onClick={loadMore}
              disabled={loadingMore}
              variant="outline"
              size="sm"
              data-testid="load-more-emails"
              className="border-white/10 hover:bg-white/5 font-mono-tech text-[10px] uppercase"
            >
              {loadingMore ? "Loading…" : "Load older events"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
// ---------- MARGIN TAB ----------
function MarginTab() {
  const [data, setData] = useState(null);
  const [busyResync, setBusyResync] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/margin");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load margin data");
    }
  };
  useEffect(() => { load(); }, []);

  const resync = async () => {
    setBusyResync(true);
    try {
      const { data } = await api.post("/admin/providers/resync-defaults");
      toast.success(`Resynced ${data.resynced} providers to code defaults`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resync failed");
    } finally { setBusyResync(false); }
  };

  if (!data) return <div className="text-zinc-500 text-sm py-10">Loading margin data…</div>;

  const worstMargin = data.providers.filter((p) => p.worst_case_margin_pct != null).reduce(
    (min, p) => (p.worst_case_margin_pct < min ? p.worst_case_margin_pct : min),
    100
  );
  const hasAny = data.providers.some((p) => p.worst_case_margin_pct != null);
  const marginColor = !hasAny ? "amber" : worstMargin >= 95 ? "emerald" : worstMargin >= 80 ? "amber" : "red";

  return (
    <div className="space-y-6">
      {/* Headline stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="WORST · CASE · €/CREDIT" value={`€${data.worst_case_eur_per_credit}`} testid="stat-worst-eurcr" />
        <StatCard label="LOWEST · PROVIDER · MARGIN"
          value={hasAny ? `${worstMargin.toFixed(1)}%` : "—"}
          color={marginColor}
          testid="stat-lowest-margin" />
        <StatCard label="30D · REVENUE" value={`€${data.historical_30d.revenue_eur.toFixed(0)}`} testid="stat-30d-rev" />
        <StatCard
          label="30D · GROSS · MARGIN"
          value={data.historical_30d.margin_pct != null ? `${data.historical_30d.margin_pct.toFixed(1)}%` : "—"}
          color={data.historical_30d.margin_pct >= 90 ? "emerald" : "amber"}
          testid="stat-30d-margin" />
      </div>

      {/* Provider margin table */}
      <div className="bg-[#121214] border border-white/5 rounded-lg overflow-hidden">
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="font-mono-tech text-[10px] text-amber-400">PROVIDER · MARGIN · MATRIX</div>
            <div className="font-heading text-lg font-semibold tracking-tight mt-1">Per-provider gross margin</div>
            <div className="text-xs text-zinc-500 mt-1">Worst-case column uses the cheapest €/credit any customer can achieve (currently {data.scenarios[0]?.label}).</div>
          </div>
          <Button onClick={resync} disabled={busyResync} data-testid="resync-providers"
            className="bg-white/10 hover:bg-white/15 border border-white/10">
            {busyResync ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Resync defaults
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="margin-table">
            <thead className="bg-white/[0.03]">
              <tr className="text-left">
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">PROVIDER</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">AI COST</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">CREDITS/CLIP</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">WORST REV</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">WORST MARGIN</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {data.providers.map((p) => {
                const m = p.worst_case_margin_pct;
                const isFree = p.ai_cost_eur === 0;
                const color = isFree
                  ? "text-zinc-500"
                  : m >= 95 ? "text-emerald-400"
                  : m >= 80 ? "text-amber-400"
                  : "text-red-400";
                return (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`margin-row-${p.id}`}>
                    <td className="p-3">
                      <div className="font-medium text-white">{p.name}</div>
                      <div className="text-[10px] text-zinc-500 uppercase">{p.engine}</div>
                    </td>
                    <td className="p-3 font-mono-tech text-xs">€{p.ai_cost_eur.toFixed(2)}</td>
                    <td className="p-3 font-mono-tech text-xs">{p.credits_per_clip}</td>
                    <td className="p-3 font-mono-tech text-xs">€{p.worst_case_revenue_eur.toFixed(2)}</td>
                    <td className={`p-3 font-heading font-bold ${color}`}>
                      {isFree ? "FREE" : `${m?.toFixed(2)}%`}
                    </td>
                    <td className="p-3">
                      {p.enabled ? (
                        <span className="text-emerald-400 text-xs font-mono-tech">ACTIVE</span>
                      ) : (
                        <span className="text-zinc-500 text-xs font-mono-tech">OFF</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing scenarios table */}
      <div className="bg-[#121214] border border-white/5 rounded-lg overflow-hidden">
        <div className="p-5 border-b border-white/5">
          <div className="font-mono-tech text-[10px] text-amber-400">PRICING · SCENARIOS</div>
          <div className="font-heading text-lg font-semibold tracking-tight mt-1">€/credit across every purchase path</div>
          <div className="text-xs text-zinc-500 mt-1">Sorted cheapest → most expensive. Cheapest is your worst-case for margin.</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03]">
            <tr className="text-left">
              <th className="p-3 font-mono-tech text-[10px] text-zinc-500">SCENARIO</th>
              <th className="p-3 font-mono-tech text-[10px] text-zinc-500">KIND</th>
              <th className="p-3 font-mono-tech text-[10px] text-zinc-500">€/CREDIT</th>
              <th className="p-3 font-mono-tech text-[10px] text-zinc-500">CREDITS</th>
              <th className="p-3 font-mono-tech text-[10px] text-zinc-500">REVENUE</th>
            </tr>
          </thead>
          <tbody>
            {data.scenarios.map((s, i) => (
              <tr key={s.label} className={`border-t border-white/5 hover:bg-white/[0.02] ${i === 0 ? "bg-amber-500/[0.06]" : ""}`}>
                <td className="p-3">
                  {s.label}
                  {i === 0 && <span className="ml-2 font-mono-tech text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-black">WORST CASE</span>}
                </td>
                <td className="p-3 text-xs text-zinc-400 uppercase font-mono-tech">{s.kind}</td>
                <td className="p-3 font-heading font-bold text-amber-400">€{s.eur_per_credit.toFixed(4)}</td>
                <td className="p-3 font-mono-tech text-xs">{s.total_credits.toLocaleString()}</td>
                <td className="p-3 font-mono-tech text-xs">€{s.revenue_eur.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-zinc-500 leading-relaxed max-w-3xl">
        Margins shown are <b>gross</b> (revenue minus raw AI compute cost). Net margin is lower after Stripe fees (~2.9% + €0.25), Stripe Tax
        (+0.5–3.5% if enabled), hosting, storage, refunds and support. Realistic net margin: 40–65%.
      </p>
    </div>
  );
}

// ---------- AFFILIATE TAB ----------
function AffiliateTab() {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/affiliate/history");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-zinc-500 text-sm py-10">Loading affiliate data…</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          label="TOTAL · CREDITS · REWARDED"
          value={(data.total_credits_rewarded || 0).toLocaleString()}
          color="emerald"
          testid="stat-aff-credits"
          accent
        />
        <StatCard
          label="TOTAL · TRANSACTIONS"
          value={data.transactions.length}
          testid="stat-aff-count"
        />
      </div>

      <div className="bg-[#121214] border border-white/5 rounded-lg overflow-hidden">
        <div className="p-5 border-b border-white/5">
          <div className="font-mono-tech text-[10px] text-amber-400">AFFILIATE · REWARDS · LEDGER</div>
          <div className="font-heading text-lg font-semibold tracking-tight mt-1">Credit reward history</div>
          <div className="text-xs text-zinc-500 mt-1">
            Each row is bonus credits automatically added to a referrer&apos;s balance when their referred user paid a subscription invoice.
            No manual actions — credits are granted instantly on webhook.
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="affiliate-table">
            <thead className="bg-white/[0.03]">
              <tr className="text-left">
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">DATE</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">REFERRER</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">REFERRED · USER</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500">SUBSCRIPTION · PAID</th>
                <th className="p-3 font-mono-tech text-[10px] text-zinc-500 text-right">CREDITS · AWARDED</th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-zinc-500 text-sm">No referral rewards yet.</td></tr>
              ) : (
                data.transactions.map((t) => (
                  <tr key={t.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`aff-row-${t.id}`}>
                    <td className="p-3 font-mono-tech text-[11px] text-zinc-400">
                      {new Date(t.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <div className="text-white text-xs font-medium">{t.referrer_name}</div>
                      <div className="text-[10px] text-zinc-500">{t.referrer_email}</div>
                    </td>
                    <td className="p-3 text-xs text-zinc-300">{t.referred_email}</td>
                    <td className="p-3 font-mono-tech text-xs text-zinc-400">
                      €{((t.invoice_amount_cents || 0) / 100).toFixed(2)}
                    </td>
                    <td className="p-3 text-right">
                      <span className="inline-flex items-center gap-1 font-mono-tech font-bold text-emerald-400">
                        <Coins className="w-3.5 h-3.5" strokeWidth={1.5} />
                        +{(t.credits_awarded || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-zinc-500">
        Referral rewards are <b>credits only</b> — no fiat payouts, no Stripe Connect, no KYC.
        Credits are granted on the Stripe webhook when a referred user&apos;s subscription invoice is paid.
      </p>
    </div>
  );
}

